/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Credit Top-up & Stripe Checkout Modal (Task 10.1.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useUiStore } from '../../stores/useUiStore';
import { useWorkspaceStore } from '../../stores/useWorkspaceStore';
import { useI18nStore } from '../../stores/useI18nStore';
import { formatCreditNumber } from '../../hooks/useWalletBalance';
import {
  Coins,
  X,
  Check,
  Sparkles,
  ShieldCheck,
  CreditCard,
} from 'lucide-react';

export interface TopupPackage {
  id: string;
  name: string;
  credits: number;
  bonusCredits: number;
  priceUsd: number;
  popular?: boolean;
  featureKeys: string[];
}

export const TOPUP_PACKAGES: TopupPackage[] = [
  {
    id: 'starter',
    name: 'Starter Tier',
    credits: 10000,
    bonusCredits: 0,
    priceUsd: 10,
    featureKeys: ['packageStarterDesc1', 'packageStarterDesc2', 'packageStarterDesc3'],
  },
  {
    id: 'pro-creator',
    name: 'Pro Creator',
    credits: 50000,
    bonusCredits: 5000,
    priceUsd: 39,
    popular: true,
    featureKeys: [
      'packageProDesc1',
      'packageProDesc2',
      'packageProDesc3',
      'packageProDesc4',
    ],
  },
  {
    id: 'studio-enterprise',
    name: 'Studio Enterprise',
    credits: 200000,
    bonusCredits: 40000,
    priceUsd: 129,
    featureKeys: [
      'packageEnterpriseDesc1',
      'packageEnterpriseDesc2',
      'packageEnterpriseDesc3',
      'packageEnterpriseDesc4',
      'packageEnterpriseDesc5',
    ],
  },
];

export const TopupModal: React.FC = () => {
  const { t } = useI18nStore();
  const { activeModal, openModal, closeModal } = useUiStore();
  const { getActiveWorkspace } = useWorkspaceStore();

  const [selectedPackageId, setSelectedPackageId] = useState<string>('pro-creator');

  const activeWorkspace = getActiveWorkspace();
  const isOpen = activeModal === 'topup_credits';

  if (!isOpen) {
    return null;
  }

  const selectedPackage =
    TOPUP_PACKAGES.find((p) => p.id === selectedPackageId) || TOPUP_PACKAGES[1];
  const totalCredits = selectedPackage.credits + selectedPackage.bonusCredits;

  const handleProceedToGateway = () => {
    openModal('payment_checkout', {
      itemType: 'token_pack',
      itemId: selectedPackage.id,
      name: selectedPackage.name,
      credits: totalCredits,
      amount: selectedPackage.priceUsd,
      currency: 'USD',
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="topup-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/70 backdrop-blur-md animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div className="w-full max-w-xl rounded-2xl border border-surface-border bg-surface p-6 sm:p-7 shadow-2xl animate-scale-in relative space-y-6">
        {/* Header Strip */}
        <div className="flex items-center justify-between border-b border-surface-border pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shadow-sm">
              <Coins className="w-5 h-5" />
            </div>
            <div>
              <h2 id="topup-modal-title" className="font-heading font-bold text-base text-foreground">
                {t('wallet.topupModalTitle')}
              </h2>
              <p className="text-xs text-foreground-muted">
                {activeWorkspace ? `${t('workspace.title')}: ${activeWorkspace.name}` : t('wallet.topupModalSubtitle')}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={closeModal}
            className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Packages Grid */}
        <div className="space-y-3">
          <div className="text-[11px] font-bold text-foreground-muted uppercase tracking-wider">
            {t('wallet.topUpCredits')}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {TOPUP_PACKAGES.map((pkg) => {
              const isSelected = pkg.id === selectedPackageId;
              const hasBonus = pkg.bonusCredits > 0;
              return (
                <button
                  key={pkg.id}
                  type="button"
                  onClick={() => setSelectedPackageId(pkg.id)}
                  className={`relative p-3.5 rounded-xl border text-left transition-all duration-150 flex flex-col justify-between ${
                    isSelected
                      ? 'border-primary bg-primary/5 ring-2 ring-primary/30 shadow-sm'
                      : 'border-surface-border bg-surface-elevated/50 hover:border-surface-border/80 hover:bg-surface-elevated'
                  }`}
                >
                  {pkg.popular && (
                    <span className="absolute -top-2.5 right-3 text-[9px] font-bold px-2 py-0.5 rounded-full bg-primary text-primary-fg shadow-sm uppercase tracking-wide">
                      {t('wallet.popularBadge')}
                    </span>
                  )}

                  <div className="space-y-1">
                    <div className="font-semibold text-xs text-foreground">{pkg.name}</div>
                    <div className="text-lg font-bold text-primary font-mono">${pkg.priceUsd}</div>
                    <div className="text-xs font-semibold text-foreground">
                      {formatCreditNumber(pkg.credits)}
                    </div>
                    {hasBonus && (
                      <div className="text-[10px] font-bold text-emerald-500 inline-flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        <span>+{formatCreditNumber(pkg.bonusCredits)} bonus</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 pt-2 border-t border-surface-border/60 text-[10px] text-foreground-muted">
                    {isSelected ? (
                      <span className="text-primary font-semibold flex items-center gap-1">
                        <Check className="w-3 h-3" /> {t('wallet.selectedPackage')}
                      </span>
                    ) : (
                      <span>{t('wallet.clickToSelect')}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Package Features List */}
        <div className="p-3.5 rounded-xl border border-surface-border bg-muted/30 space-y-2 text-xs">
          <div className="font-semibold text-foreground flex items-center justify-between">
            <span>{t('wallet.packageDetails', { name: selectedPackage.name })}</span>
            <span className="font-mono text-primary font-bold">
              {t('wallet.totalCredits', { count: formatCreditNumber(totalCredits) })}
            </span>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[11px] text-foreground-muted">
            {selectedPackage.featureKeys.map((key) => (
              <li key={key} className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                <span>{t(`wallet.${key}`)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-2 text-[11px] text-foreground-muted">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            <span>{t('wallet.stripeSecureNotice')}</span>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={closeModal}
              className="w-1/2 sm:w-auto px-4 py-2 rounded-xl border border-surface-border text-xs font-medium text-foreground hover:bg-muted transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleProceedToGateway}
              className="w-1/2 sm:w-auto flex items-center justify-center gap-2 px-5 py-2 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:bg-primary-hover active:scale-[0.98] transition-all shadow-md"
            >
              <CreditCard className="w-3.5 h-3.5" />
              <span>{t('wallet.payAmount', { amount: selectedPackage.priceUsd.toString() })}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
